import { GMAPS_API_KEY } from 'constants/envs'
import MapErrorBoundary from '../map-error-boundary/MapErrorBoundary'
import { APIProvider, Map } from '@vis.gl/react-google-maps'
import styles from '../new-producer-map/mapStyles.json'
import ProducerMarker from './ProducerMarker'
import { PositionsWithProducers, ProducerMapProps } from './types'

const ProducerMap = ({
  activePositionIndex,
  activeProducerId,
  center: conditionalCenter,
  enablePanning,
  height = '100%',
  onBoundsChange,
  onClick,
  onMouseEnter,
  onMouseLeave,
  onZoomChanged,
  popupPositionIndex,
  popupProducerId,
  producers,
  zoom = 8
}: ProducerMapProps) => {
  // Constants
  const center = conditionalCenter || {
    lat: 50.943383,
    lng: 3.928225
  }

  // Marker ref

  /**
   * Returns the sorted positions
   *
   * @returns PositionsWithProducers[]
   */
  const getSortedPositions = (): PositionsWithProducers[] => {
    // Filter the producers with positions
    const producersWithPositions = producers.filter((producer) => producer.coordinates)

    // Reduce the producersWithPositions to an array of PositionsWithProducers objects
    const positionsWithProducerInfo = producersWithPositions.reduce<PositionsWithProducers[]>((positionsWithProducer, producer, index) => {
      const {
        coordinates: { lat, long }
      } = producer

      positionsWithProducer.push({ index, position: { lat: Number(lat), lng: Number(long) }, producer })

      return positionsWithProducer
    }, [])

    // Sort & return the positionsWithproducers
    return positionsWithProducerInfo.slice().sort((a, b) => {
      if (
        (a.producer.id === popupProducerId && a.index === popupPositionIndex) ||
        (a.producer.id === activeProducerId && a.index === activePositionIndex)
      ) {
        return 1
      }

      if (
        (b.producer.id === popupProducerId && b.index === popupPositionIndex) ||
        (b.producer.id === activeProducerId && b.index === activePositionIndex)
      ) {
        return -1
      }

      return 0
    })
  }

  return (
    <div style={{ height, width: '100%' }}>
      <MapErrorBoundary>
        <APIProvider apiKey={GMAPS_API_KEY!}>
          <Map
            mapId="producerMap"
            style={{ width: '100%' }}
            defaultCenter={center}
            defaultZoom={zoom}
            gestureHandling={enablePanning ? 'greedy' : 'cooperative'}
            disableDefaultUI={true}
            scrollwheel={false}
            disableDoubleClickZoom={true}
            zoomControl={true}
            minZoom={8}
            maxZoom={15}
            fullscreenControl={false}
            styles={styles}
            onBoundsChanged={(e) => onBoundsChange && onBoundsChange(e.detail.bounds)}
            onClick={() => {
              if (onClick) {
                onClick()
              }
            }}
            onZoomChanged={onZoomChanged}
          >
            {getSortedPositions().map(({ position, index, producer }) => {
              if (Number.isFinite(position.lat) && Number.isFinite(position.lng)) {
                return (
                  <ProducerMarker
                    active={activeProducerId === producer.id && activePositionIndex === index}
                    key={`${producer.id}${index}`}
                    {...{ index, onClick, onMouseEnter, onMouseLeave, producer }}
                  />
                )
              }

              return null
            })}
          </Map>
        </APIProvider>
      </MapErrorBoundary>
    </div>
  )
}

export default ProducerMap
